import { defineStore } from "pinia";

export const useScheduleSearchFilter = defineStore("useScheduleSearchFilter", {
  state: (): {
    isDrawerEnabled: boolean;
    vesselName: string;
    vesselStatus: string;
    scheduleArrivalFromDate: string;
    scheduleArrivalToDate: string;
    actualArrivalFromDate: string;
    actualArrivalToDate: string;
    firstAvailableFromDate: string;
    firstAvailableToDate: string;
    scheduleDepartureFromDate: string;
    scheduleDepartureToDate: string;
    actualDepartureFromDate: string;
    actualDepartureToDate: string;
    startReceivingFromDate: string;
    startReceivingToDate: string;
    cargoCutoffFromDate: string;
    cargoCutoffToDate: string;
    reeferCutoffFromDate: string;
    reeferCutoffToDate: string;
    hazardousCutoffFromDate: string;
    hazardousCutoffToDate: string;
    vesselBerth: string;
  } => ({
    isDrawerEnabled: false,
    vesselName: "",
    vesselStatus: "",
    scheduleArrivalFromDate: "",
    scheduleArrivalToDate: "",
    actualArrivalFromDate: "",
    actualArrivalToDate: "",
    firstAvailableFromDate: "",
    firstAvailableToDate: "",
    scheduleDepartureFromDate: "",
    scheduleDepartureToDate: "",
    actualDepartureFromDate: "",
    actualDepartureToDate: "",
    startReceivingFromDate: "",
    startReceivingToDate: "",
    cargoCutoffFromDate: "",
    cargoCutoffToDate: "",
    reeferCutoffFromDate: "",
    reeferCutoffToDate: "",
    hazardousCutoffFromDate: "",
    hazardousCutoffToDate: "",
    vesselBerth: ""
  }),
  getters: {
    getVesselFilterValues(): any {
      return {
        drawerFilters: {
          vesselName: this.vesselName,
          vesselStatus: this.vesselStatus,
          vesselBerth: this.vesselBerth,
          scheduledArrivalDateTimeLocal: {
            from: this.scheduleArrivalFromDate,
            to: this.scheduleArrivalToDate
          },
          actualArrivalDateTimeLocal: {
            from: this.actualArrivalFromDate,
            to: this.actualArrivalToDate
          },
          firstAvailableDateTimeLocal: {
            from: this.firstAvailableFromDate,
            to: this.firstAvailableToDate
          },
          scheduledDepartureDateTimeLocal: {
            from: this.scheduleDepartureFromDate,
            to: this.scheduleDepartureToDate
          },
          actualDepartureDateTimeLocal: {
            from: this.actualDepartureFromDate,
            to: this.actualDepartureToDate
          },
          startReceiveDateTimeLocal: {
            from: this.startReceivingFromDate,
            to: this.startReceivingToDate
          },
          cargoCutoffDateTimeLocal: {
            from: this.cargoCutoffFromDate,
            to: this.cargoCutoffToDate
          },
          reeferCutoffDateTimeLocal: {
            from: this.reeferCutoffFromDate,
            to: this.reeferCutoffToDate
          },
          hazardousCutoffDateTimeLocal: {
            from: this.hazardousCutoffFromDate,
            to: this.hazardousCutoffToDate
          }
        }
      };
    }
  },
  actions: {
    toggleDrawer(): void {
      this.isDrawerEnabled = !this.isDrawerEnabled;
    },

    // Set vessel name
    setVesselName(name: string): void {
      this.vesselName = name;
    },

    // Set vessel status
    setVesselStatus(status: string): void {
      this.vesselStatus = status;
    },

    // Set vessel status
    setVesselBerth(berth: string): void {
      this.vesselBerth = berth;
    },

    // Set the value of schedule arrival from date
    setScheduleArrivalFromDate(date: string): void {
      this.scheduleArrivalFromDate = date;
    },

    // Set the value of schedule arrival to date
    setScheduleArrivalToDate(date: string): void {
      this.scheduleArrivalToDate = date;
    },

    // Set the value of actual arrival from date
    setActualArrivalFromDate(date: string): void {
      this.actualArrivalFromDate = date;
    },

    // Set the value of actual arrival to date
    setActualArrivalToDate(date: string): void {
      this.actualArrivalToDate = date;
    },

    // Set the value of first available from date
    setFirstAvailableFromDate(date: string): void {
      this.firstAvailableFromDate = date;
    },

    // Set the value of first available to date
    setFirstAvailableToDate(date: string): void {
      this.firstAvailableToDate = date;
    },

    // Set the value of schedule departure from date
    setScheduleDepartureFromDate(date: string): void {
      this.scheduleDepartureFromDate = date;
    },

    // Set the value of schedule departure to date
    setScheduleDepartureToDate(date: string): void {
      this.scheduleDepartureToDate = date;
    },

    // Set the value of actual departure from date
    setActualDepartureFromDate(date: string): void {
      this.actualDepartureFromDate = date;
    },

    // Set the value of actual departure to date
    setActualDepartureToDate(date: string): void {
      this.actualDepartureToDate = date;
    },

    // Set the value of start receiving from date
    setStartReceivingFromDate(date: string): void {
      this.startReceivingFromDate = date;
    },

    // Set the value of start receiving to date
    setStartReceivingToDate(date: string): void {
      this.startReceivingToDate = date;
    },

    // Set the value of cargo cutoff from date
    setCargoCutoffFromDate(date: string): void {
      this.cargoCutoffFromDate = date;
    },

    // Set the value of cargo cutoff to date
    setCargoCutoffToDate(date: string): void {
      this.cargoCutoffToDate = date;
    },

    // Set the value of reefer cutoff from date
    setReeferCutoffFromDate(date: string): void {
      this.reeferCutoffFromDate = date;
    },

    // Set the value of reefer cutoff to date
    setReeferCutoffToDate(date: string): void {
      this.reeferCutoffToDate = date;
    },

    // Set the value of hazardous cutoff from date
    setHazardousCutoffFromDate(date: string): void {
      this.hazardousCutoffFromDate = date;
    },

    // Set the value of hazardous cutoff to date
    setHazardousCutoffToDate(date: string): void {
      this.hazardousCutoffToDate = date;
    },

    // Reset the vessel filters
    clearFilters(): void {
      this.vesselName = "";
      this.vesselStatus = "";
      this.scheduleArrivalFromDate = "";
      this.scheduleArrivalToDate = "";
      this.actualArrivalFromDate = "";
      this.actualArrivalToDate = "";
      this.firstAvailableFromDate = "";
      this.firstAvailableToDate = "";
      this.scheduleDepartureFromDate = "";
      this.scheduleDepartureToDate = "";
      this.actualDepartureFromDate = "";
      this.actualDepartureToDate = "";
      this.startReceivingFromDate = "";
      this.startReceivingToDate = "";
      this.cargoCutoffFromDate = "";
      this.cargoCutoffToDate = "";
      this.reeferCutoffFromDate = "";
      this.reeferCutoffToDate = "";
      this.hazardousCutoffFromDate = "";
      this.hazardousCutoffToDate = "";
      this.vesselBerth = "";
    }
  }
});
